import React, { useCallback, useEffect, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { Empty, Loading, daysAgo, today, ui, useCampus, useIdleLocked } from "../kits/shared";

/*
	Tienda → Márgenes (sección "productMargins", 2026-09-30). Solo Super Admin, Contraloría y los roles a los que se les dé.
	Se puede vender con pérdida o con margen bajo; esto es para verlo y corregir precios o compras.
*/

type Tab = "loss" | "low" | "prices";

const pct = (value: number | null): string => value === null ? "-" : (value * 100).toFixed(1) + "%";

const Margenes: React.FC = () => {
	const { campusId, currency } = useCampus();
	const idleLocked = useIdleLocked();
	const http = HttpManager.getInstance().inventoryService;
	const [tab, setTab] = useState<Tab>("loss");
	const [from, setFrom] = useState<string>(daysAgo(90));
	const [to, setTo] = useState<string>(today());
	const [sales, setSales] = useState<any | null>(null);
	const [prices, setPrices] = useState<any[] | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		if (!campusId) return;
		setLoading(true);
		setError(null);
		try {
			if (tab === "prices") {
				setPrices((await http.riskyPrices(campusId)).data);
			} else {
				setSales((await http.marginSales(campusId, { from, to, band: tab })).data);
			}
		} catch (requestError: any) {
			setError(requestError?.response?.data?.message || "No se pudo cargar.");
		} finally {
			setLoading(false);
		}
	}, [campusId, tab, from, to]);
	useEffect(() => { load(); }, [load]);

	const money = (value: number | null) => value === null ? "-" : currency + value.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	const counts = sales?.counts || {};
	const tabs: { key: Tab; label: string; count?: number }[] = [
		{ key: "loss", label: "Vendidos con pérdida", count: counts.loss },
		{ key: "low", label: "Margen menor a 20%", count: counts.low },
		{ key: "prices", label: "Precios en riesgo" }
	];

	if (!campusId) return <div style={ui.page}>Selecciona un plantel.</div>;

	return <div style={ui.page}>
		<h2 style={ui.title}>Márgenes</h2>
		<div style={ui.tabs}>
			{tabs.map((entry) => <button key={entry.key} type="button" disabled={idleLocked}
				className={"btn rounded-pill " + (entry.key === tab ? "btn-primary" : "btn-outline-secondary")} onClick={() => setTab(entry.key)}>
				{entry.label}{entry.count ? <span style={ui.badge(entry.key === tab)}>{entry.count}</span> : null}
			</button>)}
		</div>
		{tab !== "prices" ? <div style={{ ...ui.card, ...ui.filters }}>
			<div style={ui.field("160px")}><span style={ui.caption}>Vendido desde</span>
				<input type="date" className="form-control" style={ui.input} value={from} disabled={idleLocked} onChange={(event) => setFrom(event.target.value)} /></div>
			<div style={ui.field("160px")}><span style={ui.caption}>Hasta</span>
				<input type="date" className="form-control" style={ui.input} value={to} disabled={idleLocked} onChange={(event) => setTo(event.target.value)} /></div>
			{sales ? <span style={{ ...ui.small, marginLeft: "auto" }}>{(counts.ok || 0) + (counts.low || 0) + (counts.loss || 0)} ventas con costo · {counts.noCost || 0} todavía sin costo de compra</span> : null}
		</div> : null}
		<div style={{ ...ui.card, overflowX: "auto", marginTop: "10px" }}>
			{loading ? <Loading /> : error ? <div className="alert alert-danger" style={{ margin: "16px" }}>{error}</div>
				: tab === "prices" ? (!prices || prices.length < 1 ? <Empty text="Ningún precio deja menos de 20%." /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead><tr>
						<th style={ui.th}>Producto</th>
						<th style={{ ...ui.th, ...ui.right }}>Precio de venta</th>
						<th style={{ ...ui.th, ...ui.right }}>Costo de compra</th>
						<th style={{ ...ui.th, ...ui.right }}>Margen</th>
						<th style={{ ...ui.th, ...ui.right }}>%</th>
					</tr></thead>
					<tbody>{prices.map((row) => <tr key={row.articleId}>
						<td style={ui.td}><div style={{ fontWeight: 600 }}>{row.name}</div><div style={ui.tiny}>{row.waCode} · {row.kind}</div></td>
						<td style={{ ...ui.td, ...ui.right }}>{money(row.price)}</td>
						<td style={{ ...ui.td, ...ui.right }}>{money(row.cost)}</td>
						<td style={{ ...ui.td, ...ui.right, color: "var(--wa-oro)", fontWeight: 600 }}>{money(row.margin)}</td>
						<td style={{ ...ui.td, ...ui.right, color: "var(--wa-oro)", fontWeight: 700 }}>{pct(row.marginPct)}</td>
					</tr>)}</tbody>
				</table>)
				: !sales || sales.rows.length < 1 ? <Empty text={tab === "loss" ? "Ninguna venta con pérdida en estas fechas." : "Ninguna venta con margen menor a 20% en estas fechas."} />
				: <table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead><tr>
						<th style={ui.th}>Vendido</th>
						<th style={ui.th}>Alumno</th>
						<th style={ui.th}>Producto</th>
						<th style={{ ...ui.th, ...ui.right }}>Cobrado</th>
						<th style={{ ...ui.th, ...ui.right }}>Costo</th>
						<th style={{ ...ui.th, ...ui.right }}>Margen</th>
						<th style={{ ...ui.th, ...ui.right }}>%</th>
					</tr></thead>
					<tbody>{sales.rows.map((row: any) => <tr key={row.kitId}>
						<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{row.date}<div style={ui.tiny}>{row.campus}</div></td>
						<td style={ui.td}>{row.student}</td>
						<td style={ui.td}><div style={{ fontWeight: 600 }}>{row.product}{row.quantity > 1 ? " ×" + row.quantity : ""}</div><div style={ui.tiny}>{row.waCode}</div></td>
						<td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>{money(row.revenue)}{row.discount > 0 ? <div style={ui.tiny}>lista {money(row.listPrice)} · desc. {money(row.discount)}</div> : null}</td>
						<td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>{money(row.cost)}<div style={ui.tiny}>{row.costSource}</div></td>
						<td style={{ ...ui.td, ...ui.right, color: "var(--wa-oro)", fontWeight: 600 }}>{money(row.margin)}</td>
						<td style={{ ...ui.td, ...ui.right, color: "var(--wa-oro)", fontWeight: 700 }}>{pct(row.marginPct)}</td>
					</tr>)}</tbody>
				</table>}
		</div>
	</div>;
};

export default Margenes;
